import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QuoteForm = ({ onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service_type: "",
    description: "",
    preferred_date: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (value) => {
    setFormData(prev => ({ ...prev, service_type: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/quotes`, formData);
      toast.success("Quote request submitted!", {
        description: "We'll get back to you within 24 hours.",
      });
      setFormData({
        name: "",
        email: "",
        phone: "",
        service_type: "",
        description: "",
        preferred_date: "",
      });
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error("Failed to submit quote request", {
        description: "Please try again or call us directly.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="quote-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="name">Full Name *</Label>
          <Input
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="John Doe"
            required
            className="h-12"
            data-testid="quote-name-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="phone">Phone Number *</Label>
          <Input
            id="phone"
            name="phone"
            type="tel"
            value={formData.phone}
            onChange={handleChange}
            placeholder="(708) 555-0123"
            required
            className="h-12"
            data-testid="quote-phone-input"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="email">Email Address *</Label>
        <Input
          id="email"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleChange}
          placeholder="john@example.com"
          required
          className="h-12"
          data-testid="quote-email-input"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="service_type">Service Type *</Label>
        <Select value={formData.service_type} onValueChange={handleServiceChange} required>
          <SelectTrigger className="h-12" data-testid="quote-service-select">
            <SelectValue placeholder="Select a service" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="windows">Windows Installation</SelectItem>
            <SelectItem value="doors">Doors Installation</SelectItem>
            <SelectItem value="kitchens">Kitchen Remodeling</SelectItem>
            <SelectItem value="bathrooms">Bathroom Renovation</SelectItem>
            <SelectItem value="new-construction">New Construction</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">Project Description *</Label>
        <Textarea
          id="description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          placeholder="Tell us about your project..."
          required
          rows={4}
          data-testid="quote-description-input"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="preferred_date">Preferred Contact Date (Optional)</Label>
        <Input
          id="preferred_date"
          name="preferred_date"
          type="date"
          value={formData.preferred_date}
          onChange={handleChange}
          className="h-12"
          data-testid="quote-date-input"
        />
      </div>

      <Button 
        type="submit" 
        className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-sm btn-primary"
        disabled={loading}
        data-testid="quote-submit-btn"
      >
        {loading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 spinner" />
            Submitting...
          </>
        ) : (
          "Submit Quote Request"
        )}
      </Button>
    </form>
  );
};

export default QuoteForm;
